import { useEffect, useState } from "react";
import {
    getSessionStorageItemProperty,
    updateSessionStorageItemProperty,
} from "../utils/sessionStorage";

const silentlyReplaceSearchParams = (params: string | URLSearchParams) => {
    const newUrl = `${window.location.protocol}//${window.location.host}${
        window.location.pathname
    }${params === "" ? "" : `?${params}`}`;
    window.history.replaceState(
        { ...window.history.state, newUrl },
        "",
        newUrl
    );
};

export function useStateParams<
    T extends string | number | boolean | number[] | string[] | undefined | null
>(
    initialState: T,
    paramsName: string,
    serialize: (state: T) => string,
    deserialize: (state: string) => T,
    persistInSessionStorage?: boolean
): [T, (state: ((currentValue: T) => T) | T) => void] {
    const search = new URLSearchParams(window.location.search);

    const existingQueryParameterValue = search.get(paramsName);

    const fallbackValue: T =
        getSessionStorageItemProperty(window.location.pathname, paramsName) ??
        initialState;
    const [state, setState] = useState<T>(
        existingQueryParameterValue
            ? deserialize(existingQueryParameterValue)
            : fallbackValue
    );

    useEffect(() => {
        // Updates state when user navigates backwards or forwards in browser history
        // use of JSON.stringify below to be able to compare arrays/object types
        if (
            existingQueryParameterValue &&
            JSON.stringify(deserialize(existingQueryParameterValue)) !==
                JSON.stringify(state)
        ) {
            setState(deserialize(existingQueryParameterValue));
        }
    }, [existingQueryParameterValue, deserialize, state]);

    const onChange = (valueOrUpdateFunc: ((currentValue: T) => T) | T) => {
        const handleSearchParamsUpdate = (s: T) => {
            const searchParams = new URLSearchParams(window.location.search);

            // Below checks for empty values and removes param if truthy
            if (s === "" || (Array.isArray(s) && s.length === 0)) {
                searchParams.delete(paramsName);
            } else {
                searchParams.set(paramsName, serialize(s));
            }

            silentlyReplaceSearchParams(searchParams);

            if (persistInSessionStorage) {
                updateSessionStorageItemProperty(
                    window.location.pathname,
                    paramsName,
                    s
                );
            }
        };
        if (typeof valueOrUpdateFunc === "function") {
            setState((currentValue) => {
                const newValue = valueOrUpdateFunc(currentValue);
                handleSearchParamsUpdate(newValue);
                return newValue;
            });
        } else {
            setState(valueOrUpdateFunc);
            handleSearchParamsUpdate(valueOrUpdateFunc);
        }
    };

    return [state, onChange];
}
